// src/pages/Tools/nintendo-quest/ShlokaJourney.tsx

import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Trophy, Target } from 'lucide-react';
import { ShlokaJourney as ShlokaJourneyType } from '../../../lib/nintendo-quest/types';

interface ShlokaJourneyProps {
  journey: ShlokaJourneyType;
}

export default function ShlokaJourney({ journey }: ShlokaJourneyProps) {
  const progressPercentage = (journey.currentCount / journey.targetCount) * 100;

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-purple-400" />
          Sanskrit Shloka Journey
        </h3>
        <div className="text-right">
          <div className="text-2xl font-bold text-white">{journey.currentCount}</div>
          <div className="text-sm text-white/60">of {journey.targetCount}</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-white/60 mb-2">
          <span>Progress to Temple Competition</span>
          <span>{progressPercentage.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full"
          />
        </div>
      </div>

      {/* Next Milestone */}
      <div className="bg-blue-500/20 border border-blue-400/30 rounded-lg p-4 mb-6 text-center">
        <Target className="w-6 h-6 text-blue-400 mx-auto mb-2" />
        <div className="text-blue-300 font-bold">
          {journey.nextMilestone.shlokas_needed} more shlokas to next milestone
        </div>
        <div className="text-blue-200 text-sm mt-1">
          Next goal: {journey.nextMilestone.count} shlokas
        </div>
      </div>

      {/* Milestone progression */}
      <div className="space-y-2">
        <h4 className="text-white font-medium mb-3">Milestones</h4>
        <div className="grid grid-cols-5 gap-2">
          {journey.milestones.map((milestone, index) => (
            <motion.div
              key={milestone.count}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className={`relative p-2 rounded-lg text-center ${
                milestone.achieved 
                  ? 'bg-green-500/20 border border-green-400/30' 
                  : milestone.current
                  ? 'bg-orange-500/20 border border-orange-400/30'
                  : 'bg-gray-500/10 border border-gray-400/20'
              }`}
            >
              <div className={`text-sm font-bold ${
                milestone.achieved ? 'text-green-300' : 
                milestone.current ? 'text-orange-300' : 'text-gray-400'
              }`}>
                {milestone.count}
              </div>
              {milestone.achieved && (
                <Trophy className="w-3 h-3 text-green-400 mx-auto mt-1" />
              )}
              {milestone.current && (
                <Target className="w-3 h-3 text-orange-400 mx-auto mt-1" />
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Temple Competition Status */}
      {journey.templeCompetitionReady && (
        <div className="mt-4 bg-yellow-500/20 border border-yellow-400/30 rounded-lg p-3 text-center">
          <div className="text-yellow-300 font-bold">🏛️ Temple Competition Ready!</div>
        </div>
      )}
    </div>
  );
}